import type { VideoProvider } from '../core/definitions';

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

/**
 * How an item fills the playlist's stage: 'stretch' to the stage, 'contain'
 * (whole picture, transparent bars) or 'cover' (fills, edges cropped).
 */
export type StageFit = 'stretch' | 'contain' | 'cover';

/** Largest rectangle with the source's aspect that fits inside the target, centred. */
export function containRect(sourceWidth: number, sourceHeight: number, targetWidth: number, targetHeight: number): Rect {
  return fitRect('contain', sourceWidth, sourceHeight, targetWidth, targetHeight);
}

/** Where a `source`-sized picture lands in a `target`-sized stage for a fit mode. */
export function fitRect(fit: StageFit, sourceWidth: number, sourceHeight: number, targetWidth: number, targetHeight: number): Rect {
  if (fit === 'stretch' || sourceWidth <= 0 || sourceHeight <= 0) return { x: 0, y: 0, width: targetWidth, height: targetHeight };
  const sx = targetWidth / sourceWidth;
  const sy = targetHeight / sourceHeight;
  const scale = fit === 'contain' ? Math.min(sx, sy) : Math.max(sx, sy);
  const width = sourceWidth * scale;
  const height = sourceHeight * scale;
  return { x: (targetWidth - width) / 2, y: (targetHeight - height) / 2, width, height };
}

/** True when the picture fills the stage exactly as it is, so it can be shown without redrawing. */
export function fillsStage(fit: StageFit, source: Size, stage: Size): boolean {
  if (fit === 'stretch') return true;
  return Math.abs(source.width / source.height - stage.width / stage.height) < 0.005;
}

/** Fade progress 0..1 for a cross-fade that started at `startedAt` and lasts `durationMs`. */
export function fadeProgress(now: number, startedAt: number, durationMs: number): number {
  if (durationMs <= 0) return 1;
  return Math.min(1, Math.max(0, (now - startedAt) / durationMs));
}

/** Longest side of a stage raster: enough for full-HD items, small enough to redraw every frame. */
const MAX_STAGE_RASTER = 3840;

/**
 * Draws playlist items onto a fixed stage. Every item lands on the stage
 * through the same `fitRect`, in steady state and during fades, so an item's
 * geometry never changes when a fade starts or ends.
 *
 * A fade draws the outgoing picture at weight 1-p and adds ('lighter') the
 * incoming one at weight p, so every premultiplied channel, alpha included, is
 * the linear mix out·(1-p) + in·p: the midpoint of an opaque red→blue fade is
 * (128,0,128). Where only one item covers a pixel (contain bars), that item
 * simply fades in or out over transparency.
 *
 * The canvas is the stage size scaled up to the larger item's resolution
 * (published as `pixelRatio`), so a small stage does not blur a large item.
 */
export class SlideshowBlend {
  private readonly canvas = document.createElement('canvas');
  private readonly g = this.canvas.getContext('2d');
  private version = 0;
  readonly provider: VideoProvider = { source: this.canvas, width: 1, height: 1, dynamic: false, version: 0 };

  draw(from: VideoProvider | null, to: VideoProvider, progress: number, stage: Size, fit: StageFit): void {
    const g = this.g;
    if (!g) return;
    const largest = Math.max(to.width, to.height, from?.width ?? 0, from?.height ?? 0);
    const ratio = Math.min(Math.max(1, largest / Math.max(stage.width, stage.height)), MAX_STAGE_RASTER / Math.max(stage.width, stage.height));
    const width = Math.max(1, Math.round(stage.width * ratio));
    const height = Math.max(1, Math.round(stage.height * ratio));
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
    const p = from ? Math.min(1, Math.max(0, progress)) : 1;
    g.save();
    g.clearRect(0, 0, width, height);
    if (from) {
      g.globalAlpha = 1 - p;
      const a = fitRect(fit, from.width, from.height, width, height);
      g.drawImage(from.source as CanvasImageSource, a.x, a.y, a.width, a.height);
      g.globalCompositeOperation = 'lighter';
    }
    g.globalAlpha = p;
    const b = fitRect(fit, to.width, to.height, width, height);
    g.drawImage(to.source as CanvasImageSource, b.x, b.y, b.width, b.height);
    g.restore();
    this.provider.width = stage.width;
    this.provider.height = stage.height;
    if (ratio !== 1) this.provider.pixelRatio = width / stage.width;
    else delete this.provider.pixelRatio;
    this.provider.dynamic = Boolean(to.dynamic || from?.dynamic);
    this.provider.version = ++this.version;
  }

  dispose(): void {
    this.canvas.width = 0;
    this.canvas.height = 0;
  }
}

/**
 * A child's picture laid out at the stage size without redrawing it: the
 * compositor scales the child's own texture to `stage`. Reads the child's
 * provider live, because a child replaces its provider (or its `source`, for
 * frame ingestion) as it plays.
 */
export function stageView(child: () => VideoProvider | null, stage: () => Size): VideoProvider {
  // The last provider seen covers a read that races the child clearing its picture.
  let last: VideoProvider | null = null;
  const current = (): VideoProvider => {
    last = child() ?? last;
    return last ?? { source: document.createElement('canvas'), width: 1, height: 1, dynamic: false, version: 0 };
  };
  return {
    get source() {
      return current().source;
    },
    get width() {
      return stage().width;
    },
    get height() {
      return stage().height;
    },
    get dynamic() {
      return current().dynamic;
    },
    get version() {
      return current().version;
    },
  } as VideoProvider;
}
